import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Settings2, Download, Play, Server, Eye, Paperclip, X, ShieldCheck, RotateCw, Trash2, Check, AlertTriangle, RefreshCw, Zap, Stethoscope, Loader2 } from "lucide-react";

const EXAMPLE = `Hi ForensiQ team,

We noticed suspicious traffic from 185.220.101.45 hitting our web servers with an SQL injection payload. Also seeing DNS lookups to phishing-malicious-example.com from finance workstations.

A colleague also mentioned CVE-2024-3094 concerns. Attached hash 44d88612fea8a8f36de82e1278abb02f was detected on wks-hr-05.

Please investigate and send a report.
Regards, SOC lead`;

export default function EmailInvestigator() {
  const [form, setForm] = useState({
    from_email: "ciso@acme.com",
    from_name: "Acme CISO",
    subject: "Suspicious activity — need urgent investigation",
    body: EXAMPLE,
    industry: "Financial Services",
    severity: "high",
    send_reply: false,
  });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [processed, setProcessed] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileRef = useRef(null);

  const loadHistory = async () => {
    try { const { data } = await api.get("/email/processed"); setProcessed(data); }
    catch (_e) { /* ok */ }
  };
  const loadCfg = async () => {
    try { const { data } = await api.get("/email/config"); setCfg(data); }
    catch (_e) { /* ok */ }
  };
  useEffect(() => { loadHistory(); loadCfg(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setRunning(true); setResult(null);
    try {
      let data;
      if (attachments.length > 0) {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
        attachments.forEach((f) => fd.append("files", f));
        const token = localStorage.getItem("forensiq_token");
        const res = await fetch(`${API_BASE}/email/simulate-with-files`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) throw new Error((await res.json()).detail || "Investigation failed");
        data = await res.json();
      } else {
        const r = await api.post("/email/simulate", form);
        data = r.data;
      }
      setResult(data);
      loadHistory();
      toast.success(`Case ${data.case_number} — ${data.verdict?.replace("_", " ")}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Investigation failed");
    } finally { setRunning(false); }
  };

  const onFilesPicked = (fl) => {
    const arr = Array.from(fl || []).slice(0, 5);
    setAttachments((prev) => [...prev, ...arr].slice(0, 5));
    if (fileRef.current) fileRef.current.value = "";
  };
  const removeFile = (i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  const saveCfg = async (updated) => {
    try {
      await api.put("/email/config", updated);
      toast.success("Email config saved");
      loadCfg();
    } catch (_e) { toast.error("Save failed"); }
  };

  const downloadPdf = async (invId, caseNumber) => {
    const token = localStorage.getItem("forensiq_token");
    const res = await fetch(`${API_BASE}/investigations/${invId}/report.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Download failed");
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ForensiQ-${caseNumber}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" /> Email → Investigation → Report
          </div>
          <h1 className="font-display font-black text-5xl mt-2">Email Investigator</h1>
          <p className="text-[#8E9196] mt-1">Forward any suspicious email — attached logs, IPs, hashes, CVEs, malware questions — and ForensiQ replies with a full investigation report.</p>
        </div>
        <button onClick={() => setShowCfg(!showCfg)} data-testid="toggle-email-config"
          className="inline-flex items-center gap-2 border border-white/15 text-[#8E9196] hover:text-white px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
          <Settings2 className="w-3.5 h-3.5" /> {cfg?.enabled ? "Live inbox" : "Configure mailbox"}
        </button>
      </header>

      {showCfg && cfg && <EmailConfigPanel cfg={cfg} onSave={saveCfg} />}
      {showCfg && cfg && <DiagnosticsPanel />}
      {showCfg && cfg && <DeliverabilityStrip />}
      {showCfg && cfg && <BouncesPanel />}
      {showCfg && cfg && <VerifiedSendersPanel />}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <form onSubmit={submit} className="lg:col-span-3 border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="email-simulate-form">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-4">Simulate an incoming email</div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <FormField label="From (email)">
              <input required type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                data-testid="email-from" className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
            </FormField>
            <FormField label="From (name)">
              <input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
            </FormField>
          </div>
          <FormField label="Subject">
            <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="email-subject" className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
          </FormField>
          <FormField label="Email body (attach logs / IPs / hashes / CVEs inline)">
            <textarea required rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="email-body"
              className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF] leading-relaxed" />
          </FormField>

          <FormField label="Log attachments (JSON / syslog / CSV / .log · up to 5)">
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => fileRef.current?.click()}
                data-testid="email-file-picker"
                className="inline-flex items-center gap-1.5 border border-white/15 text-[#8E9196] hover:text-white px-3 py-1.5 rounded-sm font-mono uppercase text-[10px] tracking-widest">
                <Paperclip className="w-3 h-3" /> Attach files
              </button>
              <input ref={fileRef} type="file" multiple accept=".json,.csv,.log,.txt,.xml,.conf,.ini"
                onChange={(e) => onFilesPicked(e.target.files)} className="hidden" />
              {attachments.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1 border border-white/15 rounded-sm px-2 py-0.5 font-mono text-[10px]">
                  {a.name} <button type="button" onClick={() => removeFile(i)} className="text-[#8E9196] hover:text-[#FF3B30]"><X className="w-3 h-3" /></button>
                </span>
              ))}
              {attachments.length === 0 && <span className="text-[10px] font-mono text-[#52525B]">Content will be scanned for IOCs</span>}
            </div>
          </FormField>
          <div className="grid grid-cols-3 gap-3 mt-1">
            <FormField label="Industry">
              <input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
            </FormField>
            <FormField label="Severity">
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
                className="w-full bg-[#0a0a0a] border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]">
                {["critical","high","medium","low"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="Send email reply">
              <button type="button" onClick={() => setForm({ ...form, send_reply: !form.send_reply })}
                data-testid="email-reply-toggle"
                className={`w-full py-2 rounded-sm font-mono text-xs uppercase tracking-widest border ${
                  form.send_reply ? "border-[#00F0FF] text-[#00F0FF]" : "border-white/15 text-[#8E9196]"
                }`}>{form.send_reply ? "YES — requires SMTP" : "no"}</button>
            </FormField>
          </div>
          <div className="mt-5 flex justify-end">
            <button disabled={running} data-testid="run-email-investigation"
              className="tracing-beam inline-flex items-center gap-2 border border-[#00F0FF] bg-[#00F0FF] text-black hover:bg-[#00F0FF]/80 px-5 py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-50">
              <Play className="w-4 h-4" /> {running ? "Investigating…" : "Investigate & draft reply"}
            </button>
          </div>
        </form>

        <div className="lg:col-span-2 space-y-4">
          {result && (
            <div className="border border-[#00F0FF]/40 bg-[#00F0FF]/[0.03] rounded-sm p-5" data-testid="email-result">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF]">Investigation complete</div>
              <div className="font-display text-2xl mt-1">{result.case_number}</div>
              <div className="mt-2 text-sm text-[#E5E5E5]">
                Verdict: <span className="font-bold text-white uppercase">{result.verdict?.replace("_", " ")}</span> · confidence {result.confidence}%
              </div>
              <div className="mt-3 text-xs font-mono text-[#8E9196]">
                IOCs detected: {result.iocs_detected.length} · CVEs mentioned: {result.cves_detected.length || 0}
              </div>
              {result.reply?.sent && (
                <div className="mt-3 text-xs text-[#34C759] font-mono">✓ Reply sent via SMTP</div>
              )}
              {result.reply && !result.reply.sent && result.reply.reason && (
                <div className="mt-3 text-xs text-[#FFCC00] font-mono">⚠ Reply not sent: {result.reply.reason}</div>
              )}
              <div className="mt-4 flex gap-2 flex-wrap">
                <button onClick={() => downloadPdf(result.investigation_id, result.case_number)}
                  data-testid="download-email-pdf"
                  className="inline-flex items-center gap-1.5 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-1.5 rounded-sm font-mono uppercase text-[10px] tracking-widest">
                  <Download className="w-3 h-3" /> PDF
                </button>
                <a href={`${API_BASE}/email/preview/${result.investigation_id}.html`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 border border-white/15 text-[#8E9196] hover:text-white px-3 py-1.5 rounded-sm font-mono uppercase text-[10px] tracking-widest">
                  <Eye className="w-3 h-3" /> HTML
                </a>
              </div>
              <div className="mt-4 border border-white/10 rounded-sm overflow-hidden">
                <div className="px-3 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">HTML email body preview</div>
                <iframe title="report" srcDoc={result.html} className="w-full h-96 bg-[#060606]" />
              </div>
            </div>
          )}

          <div className="border border-white/10 bg-[#121212] rounded-sm">
            <div className="px-4 py-3 border-b border-white/10 text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Processed emails</div>
            <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
              {processed.length === 0 && <div className="text-xs text-[#52525B] p-4">No processed emails yet.</div>}
              {processed.map((p) => (
                <Link
                  key={p.id}
                  to={p.investigation_id ? `/investigations/${p.investigation_id}` : "#"}
                  onClick={(e) => { if (!p.investigation_id) e.preventDefault(); }}
                  data-testid={`processed-${p.id}`}
                  className={`block px-4 py-2 text-xs transition-colors ${
                    p.investigation_id
                      ? "hover:bg-white/5 cursor-pointer"
                      : "opacity-70 cursor-not-allowed"
                  }`}>
                  <div className="font-mono text-[#00F0FF] hover:underline">{p.case_number || p.id}</div>
                  <div className="text-white truncate">{p.subject}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">
                    {p.from_email} · {p.iocs_detected} IOCs · {new Date(p.processed_at).toLocaleString()}
                    {!p.investigation_id && p.rejected_reason && (
                      <span className="text-[#FF3B30]"> · rejected: {p.rejected_reason}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="mb-3">
      <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function SourceBadge({ source }) {
  if (source === "platform") {
    return <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-[#B57BFF]/40 bg-[#B57BFF]/[0.08] text-[9px] font-mono uppercase tracking-widest text-[#B57BFF]" data-testid="badge-platform-default">Platform default</span>;
  }
  if (source === "tenant") {
    return <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-[#00F0FF]/40 bg-[#00F0FF]/[0.08] text-[9px] font-mono uppercase tracking-widest text-[#00F0FF]">Custom</span>;
  }
  return <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-[#FFC542]/40 bg-[#FFC542]/[0.08] text-[9px] font-mono uppercase tracking-widest text-[#FFC542]">Not set</span>;
}

function EmailConfigPanel({ cfg, onSave }) {
  const [f, setF] = useState({
    imap_host: cfg.imap_host || "",
    imap_port: cfg.imap_port || 993,
    imap_user: cfg.imap_user || "",
    imap_password: "",
    imap_mailbox: cfg.imap_mailbox || "INBOX",
    smtp_host: cfg.smtp_host || "",
    smtp_port: cfg.smtp_port || 587,
    smtp_user: cfg.smtp_user || "",
    smtp_password: "",
    from_address: cfg.from_address || "",
    enabled: !!cfg.enabled,
    require_sender_verification: cfg.require_sender_verification !== false,
    allowed_senders_raw: (cfg.allowed_senders || []).join("\n"),
  });

  const submit = () => {
    const allowed_senders = (f.allowed_senders_raw || "")
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const { allowed_senders_raw, ...rest } = f;
    onSave({ ...rest, allowed_senders });
  };

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const testConn = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/email/config/test");
      setTestResult(r.data);
      const ok = r.data.imap?.ok && r.data.smtp?.ok;
      (ok ? toast.success : toast.error)(
        `IMAP ${r.data.imap?.ok ? "✓" : "✗"} · SMTP ${r.data.smtp?.ok ? "✓" : "✗"}`,
        { description: `${r.data.imap?.detail}\n${r.data.smtp?.detail}`, duration: 6000 }
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const eff = cfg.effective || {};
  const inboundAlias = eff.inbound_alias;

  const SourceBadgeLocal = SourceBadge;

  return (
    <div className="mb-6 border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="email-config-panel">
      <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2 mb-3">
        <Server className="w-3.5 h-3.5" /> IMAP + SMTP configuration
      </div>

      {inboundAlias && (
        <div className="mb-4 border border-[#00F0FF]/30 bg-[#00F0FF]/[0.04] rounded-sm p-3" data-testid="inbound-alias-callout">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-1">Give your users this address</div>
          <div className="flex items-center gap-2">
            <code className="text-white font-mono text-sm break-all" data-testid="inbound-alias">{inboundAlias}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(inboundAlias); toast.success("Copied"); }}
              data-testid="inbound-alias-copy"
              className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white"
            >
              Copy
            </button>
          </div>
          <div className="mt-1.5 text-[10px] text-[#8E9196]">
            Emails sent here are auto-routed to <span className="text-white font-mono">this tenant</span> only. Cross-tenant leakage is architecturally impossible.
          </div>
        </div>
      )}

      <p className="text-xs text-[#8E9196] mb-4">Add your mailbox credentials to enable real inbox polling and automatic email replies. Leave a section blank to inherit the <span className="text-[#B57BFF]">platform default</span> (managed by the super-admin).</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="font-display text-lg mb-2 flex items-center">Inbound (IMAP) <SourceBadge source={eff.imap_source} /></div>
          <input placeholder="host (imap.gmail.com)" value={f.imap_host} onChange={(e) => setF({ ...f, imap_host: e.target.value })} data-testid="email-imap-host" className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs mb-2 outline-none focus:border-[#00F0FF]" />
          <div className="flex gap-2 mb-2">
            <input placeholder="port" type="number" value={f.imap_port} onChange={(e) => setF({ ...f, imap_port: parseInt(e.target.value) })} data-testid="email-imap-port" className="w-24 bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
            <input placeholder="mailbox" value={f.imap_mailbox} onChange={(e) => setF({ ...f, imap_mailbox: e.target.value })} data-testid="email-imap-mailbox" className="flex-1 bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
          </div>
          <input placeholder="user" value={f.imap_user} onChange={(e) => setF({ ...f, imap_user: e.target.value })} data-testid="email-imap-user" className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs mb-2 outline-none focus:border-[#00F0FF]" />
          <input type="password" placeholder={cfg.imap_password_set ? "•••• (unchanged)" : "app password"} value={f.imap_password} onChange={(e) => setF({ ...f, imap_password: e.target.value })} data-testid="email-imap-password" className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
        </div>
        <div>
          <div className="font-display text-lg mb-2 flex items-center">Outbound (SMTP) <SourceBadge source={eff.smtp_source} /></div>
          <input placeholder="host (smtp.gmail.com)" value={f.smtp_host} onChange={(e) => setF({ ...f, smtp_host: e.target.value })} data-testid="email-smtp-host" className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs mb-2 outline-none focus:border-[#00F0FF]" />
          <div className="flex gap-2 mb-2">
            <input placeholder="port" type="number" value={f.smtp_port} onChange={(e) => setF({ ...f, smtp_port: parseInt(e.target.value) })} data-testid="email-smtp-port" className="w-24 bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
            <input placeholder="from address (noreply@corp.io)" value={f.from_address} onChange={(e) => setF({ ...f, from_address: e.target.value })} data-testid="email-from-address" className="flex-1 bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
          </div>
          <input placeholder="user" value={f.smtp_user} onChange={(e) => setF({ ...f, smtp_user: e.target.value })} data-testid="email-smtp-user" className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs mb-2 outline-none focus:border-[#00F0FF]" />
          <input type="password" placeholder={cfg.smtp_password_set ? "•••• (unchanged)" : "app password"} value={f.smtp_password} onChange={(e) => setF({ ...f, smtp_password: e.target.value })} data-testid="email-smtp-password" className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
        </div>
      </div>

      {/* Sender allowlist — critical for prod: only these senders can trigger investigations */}
      <div className="mt-5 border-t border-white/10 pt-4">
        <div className="font-display text-lg mb-1">Allowed senders</div>
        <p className="text-xs text-[#8E9196] mb-3">
          Only these addresses/domains may trigger investigations. Others are logged as
          <span className="text-[#FFC542]"> rejected</span> but no case is opened. One entry per line — use
          <span className="text-[#00F0FF]"> soc@acme.io</span> for an exact address,
          <span className="text-[#00F0FF]"> *@acme.io</span> for a whole domain, or
          <span className="text-[#00F0FF]"> acme.io</span> as a shortcut for the same. Leave empty to allow anyone (NOT recommended in production).
        </p>
        <textarea
          rows={4}
          value={f.allowed_senders_raw}
          onChange={(e) => setF({ ...f, allowed_senders_raw: e.target.value })}
          data-testid="email-allowed-senders"
          placeholder={"soc@acme.io\n*@acme.io\nsecurity@partner.example"}
          className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF] whitespace-pre resize-y"
        />
        {(cfg.allowed_senders || []).length === 0 && (
          <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#FFC542]">
            ⚠ Allowlist is empty — any sender who emails your inbox can currently open an investigation.
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-xs text-[#8E9196]">
            <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid="email-enable-poll" /> Enable auto-poll every 60s
          </label>
          <label className="flex items-center gap-2 text-xs text-[#8E9196]" title="Send a magic-link email to every new sender before opening their first case">
            <input type="checkbox" checked={f.require_sender_verification} onChange={(e) => setF({ ...f, require_sender_verification: e.target.checked })} data-testid="email-require-verification" />
            <span>Require magic-link verification for new senders <span className="text-[#00F0FF] font-mono text-[10px]">(anti-phishing)</span></span>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={submit} data-testid="save-email-config"
            className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
            <Send className="w-3.5 h-3.5" /> Save configuration
          </button>
          <button onClick={testConn} data-testid="test-email-connection" disabled={testing}
            className="inline-flex items-center gap-2 border border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black disabled:opacity-40 px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
            <Zap className="w-3.5 h-3.5" /> {testing ? "Testing…" : "Test connection"}
          </button>
          {testResult && (
            <div className="text-xs font-mono" data-testid="test-email-result">
              <span className={testResult.imap?.ok ? "text-[#34C759]" : "text-[#FF3B30]"}>
                IMAP {testResult.imap?.ok ? "✓" : "✗"}
              </span>
              <span className="mx-1 text-[#52525B]">·</span>
              <span className={testResult.smtp?.ok ? "text-[#34C759]" : "text-[#FF3B30]"}>
                SMTP {testResult.smtp?.ok ? "✓" : "✗"}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VerifiedSendersPanel() {
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addEmail, setAddEmail] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/email/verified-senders");
      setSenders(data.senders || []);
    } catch (_e) { /* ok */ }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const statusColor = (s) => ({
    verified: "#00F0FF", pending: "#FFC542", revoked: "#8E9196", expired: "#FF6B35",
  }[s] || "#8E9196");

  async function markVerified(email) {
    try {
      await api.patch(`/email/verified-senders/${encodeURIComponent(email)}`, { status: "verified" });
      toast.success(`${email} verified`);
      await load();
    } catch (_e) { toast.error("Update failed"); }
  }
  async function resend(email) {
    try {
      const { data } = await api.post(`/email/verified-senders/${encodeURIComponent(email)}/resend`);
      if (data.resent) toast.success(`Magic-link re-sent to ${email}`);
      else toast.error(`Could not send: ${data.reason || data.error || "SMTP not configured"}`);
      await load();
    } catch (_e) { toast.error("Send failed"); }
  }
  async function remove(email) {
    if (!window.confirm(`Remove verification for ${email}? They'll need to re-verify on their next email.`)) return;
    try {
      await api.delete(`/email/verified-senders/${encodeURIComponent(email)}`);
      toast.success("Removed");
      await load();
    } catch (_e) { toast.error("Delete failed"); }
  }
  async function preapprove() {
    const e = addEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.error("Enter a valid email"); return; }
    try {
      await api.patch(`/email/verified-senders/${encodeURIComponent(e)}`, { status: "verified" });
      toast.success(`${e} pre-approved`);
      setAddEmail("");
      await load();
    } catch (_e) { toast.error("Failed"); }
  }

  return (
    <div className="mb-6 border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="verified-senders-panel">
      <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2 mb-1">
        <ShieldCheck className="w-3.5 h-3.5" /> Verified senders
      </div>
      <p className="text-xs text-[#8E9196] mb-4">
        The first email from every new sender triggers a magic-link confirmation. Only <span className="text-[#00F0FF]">verified</span> addresses can open cases. Pre-approve trusted teammates below to skip the confirmation step.
      </p>

      <div className="flex gap-2 mb-4">
        <input
          value={addEmail}
          onChange={(e) => setAddEmail(e.target.value)}
          placeholder="alice@yourcompany.io — pre-approve without magic link"
          data-testid="verified-preapprove-input"
          className="flex-1 bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]"
        />
        <button
          onClick={preapprove}
          data-testid="verified-preapprove-btn"
          className="inline-flex items-center gap-1 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-[10px] tracking-widest"
        >
          <Check className="w-3.5 h-3.5" /> Pre-approve
        </button>
      </div>

      <div className="border border-white/10 rounded-sm" data-testid="verified-senders-list">
        <div className="grid grid-cols-[1.5fr_120px_150px_150px_120px] px-3 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#52525B]">
          <div>Email</div>
          <div>Status</div>
          <div>Requested</div>
          <div>Verified</div>
          <div></div>
        </div>
        {loading && <div className="p-4 text-[11px] font-mono text-[#52525B]">Loading…</div>}
        {!loading && senders.length === 0 && (
          <div className="p-4 text-[11px] font-mono text-[#52525B]">No senders yet. The first inbound email from an allowlisted address will populate this list.</div>
        )}
        {senders.map((s) => (
          <div key={s.email}
               className="grid grid-cols-[1.5fr_120px_150px_150px_120px] items-center px-3 py-2 border-b border-white/[0.04] last:border-0 text-xs"
               data-testid={`verified-sender-${s.email}`}>
            <div className="font-mono truncate" title={s.email}>
              {s.email}
              {s.last_bounce_at && (
                <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-[#FF2E63]" title={s.last_bounce_reason || "delivery failed"} data-testid={`bounce-warning-${s.email}`}>
                  <AlertTriangle className="w-3 h-3" /> bounced
                </span>
              )}
            </div>
            <div>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest"
                    style={{ color: statusColor(s.status) }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor(s.status) }} />
                {s.status}
              </span>
              {s.attempts > 1 && s.status !== "verified" && (
                <div className="text-[9px] font-mono text-[#52525B]">{s.attempts} tries</div>
              )}
            </div>
            <div className="text-[10px] font-mono text-[#8E9196]">
              {s.requested_at ? new Date(s.requested_at).toLocaleString() : "—"}
            </div>
            <div className="text-[10px] font-mono text-[#8E9196]">
              {s.verified_at ? new Date(s.verified_at).toLocaleString() : "—"}
            </div>
            <div className="flex justify-end gap-1">
              {s.status !== "verified" && (
                <button
                  onClick={() => markVerified(s.email)}
                  data-testid={`verified-approve-${s.email}`}
                  title="Force-verify without magic link"
                  className="p-1.5 border border-white/10 hover:border-[#00F0FF] hover:text-[#00F0FF] rounded-sm text-[#8E9196] transition-colors">
                  <Check className="w-3 h-3" />
                </button>
              )}
              {s.status !== "verified" && (
                <button
                  onClick={() => resend(s.email)}
                  data-testid={`verified-resend-${s.email}`}
                  title="Regenerate magic link and re-send"
                  className="p-1.5 border border-white/10 hover:border-[#FFC542] hover:text-[#FFC542] rounded-sm text-[#8E9196] transition-colors">
                  <RotateCw className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => remove(s.email)}
                data-testid={`verified-remove-${s.email}`}
                title="Delete verification"
                className="p-1.5 border border-white/10 hover:border-[#FF2E63] hover:text-[#FF2E63] rounded-sm text-[#8E9196] transition-colors">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function DeliverabilityStrip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function run() {
    setLoading(true);
    try {
      const r = await api.get("/email/deliverability");
      setData(r.data);
    } catch (_e) { setData(null); }
    finally { setLoading(false); }
  }
  useEffect(() => { run(); }, []);

  const color = (s) => ({ pass: "#00F0FF", warn: "#FFC542", fail: "#FF2E63" }[s] || "#8E9196");
  const label = (s) => ({ pass: "PASS", warn: "WARN", fail: "FAIL" }[s] || "UNKNOWN");

  return (
    <div className="mb-6 border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="deliverability-strip">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" /> Deliverability health {data?.domain && (
            <span className="text-white font-normal normal-case">· <span className="font-mono">{data.domain}</span></span>
          )}
        </div>
        <button onClick={run} disabled={loading} data-testid="deliverability-refresh"
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white disabled:opacity-40">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Re-check
        </button>
      </div>
      {loading && <div className="text-[11px] font-mono text-[#52525B]">Resolving DNS…</div>}
      {!loading && !data?.configured && (
        <div className="text-[11px] font-mono text-[#8E9196]">
          {data?.message || "Configure SMTP first to run deliverability checks."}
        </div>
      )}
      {!loading && data?.configured && (
        <>
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: "spf", name: "SPF", d: data.spf },
              { k: "dkim", name: "DKIM", d: data.dkim },
              { k: "dmarc", name: "DMARC", d: data.dmarc },
            ].map((row) => (
              <div key={row.k} className="border border-white/10 rounded-sm p-3" data-testid={`deliverability-${row.k}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">{row.name}</div>
                  <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border"
                        style={{ color: color(row.d?.status), borderColor: color(row.d?.status) + "60" }}>
                    {label(row.d?.status)}
                  </span>
                </div>
                {row.d?.selector && <div className="text-[9px] font-mono text-[#B57BFF]">selector: {row.d.selector}</div>}
                {row.d?.policy && <div className="text-[9px] font-mono text-[#B57BFF]">policy: {row.d.policy}</div>}
                {row.d?.record && (
                  <div className="mt-1 text-[10px] font-mono text-[#8E9196] break-all line-clamp-2" title={row.d.record}>
                    {row.d.record.slice(0, 120)}{row.d.record.length > 120 ? "…" : ""}
                  </div>
                )}
                {row.d?.hint && (
                  <div className="mt-1 text-[10px] text-[#FFC542]">{row.d.hint}</div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] font-mono text-[#52525B]">
            Score {data.score}/100 · checked {data.checked_at ? new Date(data.checked_at).toLocaleTimeString() : "just now"}
          </div>
        </>
      )}
    </div>
  );
}

function BouncesPanel() {
  const [bounces, setBounces] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/email/bounces");
      setBounces(data.bounces || []);
    } catch (_e) { /* ok */ }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function resolve(id) {
    try {
      await api.post(`/email/bounces/${id}/resolve`);
      toast.success("Marked resolved");
      await load();
    } catch (_e) { toast.error("Failed"); }
  }

  const openBounces = bounces.filter((b) => !b.resolved);

  return (
    <div className="mb-6 border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="bounces-panel">
      <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2 mb-1">
        <AlertTriangle className="w-3.5 h-3.5" /> Delivery failures
        {openBounces.length > 0 && (
          <span className="ml-1 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-[#FF2E63]/40 text-[#FF2E63]" data-testid="bounces-open-count">
            {openBounces.length} open
          </span>
        )}
      </div>
      <p className="text-xs text-[#8E9196] mb-3">Bounces detected on the inbound stream. Each row shows the affected recipient so you can fix the address or remove them from the allowlist.</p>
      <div className="border border-white/10 rounded-sm">
        <div className="grid grid-cols-[1.5fr_120px_1fr_100px_100px] px-3 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#52525B]">
          <div>Recipient</div>
          <div>Status</div>
          <div>Reason</div>
          <div>When</div>
          <div></div>
        </div>
        {loading && <div className="p-3 text-[11px] font-mono text-[#52525B]">Loading…</div>}
        {!loading && bounces.length === 0 && (
          <div className="p-3 text-[11px] font-mono text-[#52525B]">No bounces yet — outbound mail is healthy.</div>
        )}
        {bounces.map((b) => (
          <div key={b.id} className="grid grid-cols-[1.5fr_120px_1fr_100px_100px] items-center px-3 py-2 border-b border-white/[0.04] last:border-0 text-xs"
               data-testid={`bounce-${b.id}`}>
            <div className="font-mono truncate" title={b.recipient}>{b.recipient || "—"}</div>
            <div className="font-mono text-[10px]" style={{ color: b.resolved ? "#8E9196" : "#FF2E63" }}>
              {b.status_code || (b.resolved ? "resolved" : "failed")}
            </div>
            <div className="text-[10px] text-[#8E9196] truncate" title={b.reason}>{b.reason || "unknown"}</div>
            <div className="text-[10px] font-mono text-[#52525B]">
              {b.detected_at ? new Date(b.detected_at).toLocaleString() : "—"}
            </div>
            <div className="flex justify-end">
              {!b.resolved && (
                <button onClick={() => resolve(b.id)}
                        data-testid={`bounce-resolve-${b.id}`}
                        className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:text-white">
                  Resolve
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* --------------------------- Diagnostics Panel --------------------------- */

function Pill({ ok, label, detail }) {
  return (
    <div className="flex items-start gap-2 min-w-0">
      <div className={`shrink-0 mt-0.5 w-2 h-2 rounded-full ${ok ? "bg-[#34C759]" : "bg-[#FF3B30]"} ${ok ? "" : "animate-pulse"}`} />
      <div className="min-w-0">
        <div className="text-xs font-semibold text-white">{label} {ok ? "OK" : "FAIL"}</div>
        <div className="text-[10px] font-mono text-[#8E9196] break-words">{detail || "—"}</div>
      </div>
    </div>
  );
}

function DiagnosticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get("/email/diagnostics");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnostics failed");
    } finally { setLoading(false); }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const r = await api.post("/email/poll-now");
      toast.success(`Fetched ${r.data.fetched} new message(s)`);
      await run(); // refresh
    } catch (e) {
      toast.error(e.response?.data?.detail || "Poll failed");
    } finally { setPolling(false); }
  };

  useEffect(() => { run(); }, []);

  const state = data?.poller_state || {};
  const summary = data?.config_summary || {};

  return (
    <div className="mb-6 border border-[#00F0FF]/30 bg-[#0a0a0a] rounded-sm p-5" data-testid="email-diagnostics-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] flex items-center gap-2">
          <Stethoscope className="w-3.5 h-3.5" /> Diagnostics · why isn&apos;t mail being processed?
        </div>
        <div className="flex gap-2">
          <button onClick={pollNow} disabled={polling || loading} data-testid="diag-poll-now"
            title="Force one IMAP poll now"
            className="inline-flex items-center gap-1.5 border border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black disabled:opacity-40 px-3 py-1.5 rounded-sm font-mono uppercase text-[10px] tracking-widest">
            {polling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            Poll now
          </button>
          <button onClick={run} disabled={loading} data-testid="diag-refresh"
            className="inline-flex items-center gap-1.5 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black disabled:opacity-40 px-3 py-1.5 rounded-sm font-mono uppercase text-[10px] tracking-widest">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>
      </div>

      {!data && !loading && (
        <div className="text-xs text-[#8E9196] py-2">Click Refresh to run.</div>
      )}

      {data && (
        <>
          {/* Live connection test */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-white/10 rounded-sm p-3 mb-3 bg-[#060606]">
            <Pill ok={data.connection_test?.imap?.ok} label="IMAP" detail={data.connection_test?.imap?.detail} />
            <Pill ok={data.connection_test?.smtp?.ok} label="SMTP" detail={data.connection_test?.smtp?.detail} />
          </div>

          {/* Suggestions — the money shot */}
          {data.suggestions?.length > 0 && (
            <div className="border border-[#FFCC00]/40 bg-[#FFCC00]/[0.06] rounded-sm p-3 mb-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFCC00] mb-1.5 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Suggested next steps
              </div>
              <ul className="space-y-1.5" data-testid="diag-suggestions">
                {data.suggestions.map((s, i) => (
                  <li key={i} className="text-xs text-white leading-relaxed flex gap-2">
                    <span className="text-[#FFCC00] font-mono">{String(i + 1).padStart(2, "0")}.</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Poller state */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-[11px] font-mono">
            <StatBox label="Last successful poll" value={state.last_success_at ? new Date(state.last_success_at).toLocaleString() : "—"} accent={state.last_success_at ? "#34C759" : "#8E9196"} />
            <StatBox label="Last error" value={state.last_error ? "See below" : "None"} accent={state.last_error ? "#FF3B30" : "#8E9196"} />
            <StatBox label="Total runs" value={String(state.total_runs || 0)} />
            <StatBox label="Msgs fetched (all-time)" value={String(state.total_messages_fetched || 0)} />
          </div>
          {state.last_error && (
            <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.05] rounded-sm p-3 mb-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] mb-1">Last poller error</div>
              <pre className="text-[11px] text-white font-mono overflow-x-auto whitespace-pre-wrap">{state.last_error}</pre>
              <div className="text-[10px] font-mono text-[#8E9196] mt-1">
                at {state.last_error_at && new Date(state.last_error_at).toLocaleString()}
              </div>
            </div>
          )}

          {/* Config summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <ConfigBox title="IMAP" data={summary.imap} source={summary.imap_source} />
            <ConfigBox title="SMTP" data={summary.smtp} source={summary.smtp_source} />
          </div>

          {/* Recent processed */}
          <div className="border border-white/10 rounded-sm p-3 bg-[#060606]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-2 flex items-center gap-1">
              <Mail className="w-3 h-3" /> Recent processed (last {data.recent_processed?.length || 0})
            </div>
            {(!data.recent_processed || data.recent_processed.length === 0) ? (
              <div className="text-xs text-[#8E9196] py-1">
                No messages have been processed yet. If you&apos;ve sent test mail, either it hasn&apos;t been polled yet
                (hit &lsquo;Poll now&rsquo;) or the sender isn&apos;t on the allowlist.
              </div>
            ) : (
              <ul className="space-y-1">
                {data.recent_processed.map((m) => (
                  <li key={m.id} className="flex items-start gap-2 text-[11px]">
                    <span className={`shrink-0 mt-1 w-1.5 h-1.5 rounded-full ${
                      m.investigation_id ? "bg-[#34C759]" : m.rejected_reason ? "bg-[#FF3B30]" : "bg-[#FFCC00]"
                    }`} />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-white truncate">
                        {m.from_email || "(no sender)"} · {m.subject || "(no subject)"}
                      </div>
                      <div className="text-[10px] font-mono text-[#8E9196]">
                        {m.source} · {m.processed_at && new Date(m.processed_at).toLocaleString()}
                        {m.rejected_reason && <span className="text-[#FF3B30]"> · rejected: {m.rejected_reason}</span>}
                        {m.investigation_id && <span className="text-[#34C759]"> · investigation created</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatBox({ label, value, accent = "#8E9196" }) {
  return (
    <div className="border border-white/10 rounded-sm p-2 bg-[#060606]">
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#52525B]">{label}</div>
      <div className="mt-1 text-white truncate" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function ConfigBox({ title, data, source }) {
  if (!data) return null;
  return (
    <div className="border border-white/10 rounded-sm p-3 bg-[#060606]">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center justify-between">
        <span>{title}</span>
        {source && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-sm border border-white/15 text-[#52525B]">
            source: {source}
          </span>
        )}
      </div>
      <dl className="mt-2 space-y-0.5 text-[11px] font-mono">
        <Row k="host" v={data.host || "(unset)"} />
        <Row k="port" v={String(data.port || "—")} />
        <Row k="user" v={data.user} />
        {"mailbox" in data && <Row k="mailbox" v={data.mailbox || "INBOX"} />}
        {"from" in data && <Row k="from" v={data.from || "(unset)"} />}
        <Row k="password" v={data.password_set ? "•••••••• (set)" : <span className="text-[#FF3B30]">NOT SET</span>} />
      </dl>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-2">
      <dt className="text-[#8E9196] w-16 shrink-0">{k}</dt>
      <dd className="text-white break-all min-w-0">{v}</dd>
    </div>
  );
}

